import React, { useCallback, useEffect, useState } from 'react';
import { DollarSign, Check, Loader2, ExternalLink, AlertCircle, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  getReaderConnectStatus,
  startReaderConnectOnboarding,
  getReaderConnectDashboard,
  getReaderPayoutMethod,
  setReaderPayoutMethod,
  applyCreditToMembership,
  type ReaderPayoutMethod,
} from '@/services/readerApi';
import { PayoutCountrySelect, defaultPayoutCountry } from '@/components/payouts/PayoutCountrySelect';

type ConnectStatus = {
  connected: boolean;
  onboarded: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  country?: string;
};

const ReaderPayoutsPage: React.FC = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [payout, setPayout] = useState<ReaderPayoutMethod | null>(null);
  const [creditBusy, setCreditBusy] = useState(false);
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getReaderConnectStatus();
      setStatus(s);
    } catch (err: any) {
      setError(err?.message || 'Could not load your payout status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const loadPayout = useCallback(() => {
    getReaderPayoutMethod().then(setPayout).catch(() => setPayout(null));
  }, []);
  useEffect(() => { loadPayout(); }, [loadPayout]);

  const choosePayout = async (method: 'stripe' | 'credit') => {
    setCreditBusy(true);
    try {
      const r = await setReaderPayoutMethod(method);
      toast({
        title: method === 'credit' ? 'Your earnings will go to Hey Joe credit' : 'Your earnings will pay out through Stripe',
        description: r.creditedCents > 0 ? `${money(r.creditedCents)} was added to your credit.` : undefined,
      });
      loadPayout();
    } catch (err: any) {
      toast({ title: 'Could not update', description: err?.message, variant: 'destructive' });
    } finally {
      setCreditBusy(false);
    }
  };

  const applyToPlus = async () => {
    setCreditBusy(true);
    try {
      const r = await applyCreditToMembership();
      toast({ title: `${money(r.cents)} applied to Hey Joe Plus`, description: 'It comes off your next membership payment.' });
      loadPayout();
    } catch (err: any) {
      toast({ title: 'Could not apply credit', description: err?.message, variant: 'destructive' });
    } finally {
      setCreditBusy(false);
    }
  };

  // Handle return from Stripe onboarding.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connect = params.get('connect');
    if (connect === 'complete') {
      toast({ title: 'Thanks!', description: "We're confirming your payout setup with Stripe." });
    } else if (connect === 'refresh') {
      toast({ title: 'Setup not finished', description: 'Pick up where you left off below.' });
    }
    if (connect) {
      window.history.replaceState({}, '', '/reader/payouts');
    }
  }, [toast]);

  const handleOnboard = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await startReaderConnectOnboarding(country || defaultPayoutCountry(status?.country));
      window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'Could not start payout setup. Please try again.');
      setBusy(false);
    }
  };

  const handleDashboard = async () => {
    setBusy(true);
    try {
      const { url } = await getReaderConnectDashboard();
      window.open(url, '_blank');
    } catch (err: any) {
      toast({ title: 'Could not open dashboard', description: err?.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const isReady = !!status?.connected && !!status?.onboarded;
  const isPartial = !!status?.connected && !status?.onboarded;

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <div className="w-full" style={{ maxWidth: 460 }}>
        <div className="text-center" style={{ marginBottom: 24 }}>
          <div
            className="mx-auto flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: isReady ? 'var(--hj-green-tint, rgba(34,197,94,0.12))' : 'var(--hj-blue-tint)',
              color: isReady ? 'var(--hj-green)' : 'var(--hj-blue)',
              marginBottom: 16,
            }}
          >
            {isReady ? <Check className="h-7 w-7" /> : <DollarSign className="h-7 w-7" />}
          </div>
          <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>For readers</div>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(30px, 5vw, 42px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.03em',
              fontWeight: 800,
            }}
          >
            Get paid for your sessions.
          </h1>
          <p
            className="m-0"
            style={{ marginTop: 12, color: 'var(--hj-ink)', fontSize: 14, lineHeight: 1.5 }}
          >
            Connect your Stripe account so booking payments land in your bank. You keep{' '}
            <strong>90%</strong> of every session — Hey Joe takes a 10% platform fee.
          </p>
        </div>

        <div
          style={{
            padding: 20,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2" style={{ padding: '16px 0', color: 'var(--hj-ink-2)' }}>
              <Loader2 className="h-4 w-4 animate-spin" /> Checking your payout status…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3" style={{ padding: '8px 0' }}>
              <div className="flex items-start gap-2" style={{ fontSize: 13, color: 'hsl(var(--destructive))' }}>
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
              <button
                onClick={loadStatus}
                className="font-medium"
                style={{ fontSize: 13, color: 'var(--hj-yellow-ink)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Try again
              </button>
            </div>
          ) : isReady ? (
            <>
              <div
                className="flex items-center gap-2"
                style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-green)', marginBottom: 6 }}
              >
                <Check className="h-4.5 w-4.5" /> Your payouts are set up
              </div>
              <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                Every booking now pays out to you automatically. Any payments that were held while
                you weren't connected will be released to you.
              </p>
              <button
                onClick={handleDashboard}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 font-semibold"
                style={{
                  padding: '12px 20px',
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-2, var(--hj-bg))',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink)',
                  fontSize: 14,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                View my Stripe dashboard
              </button>
            </>
          ) : (
            <>
              {isPartial && (
                <div
                  className="flex items-start gap-2"
                  style={{
                    padding: 12,
                    marginBottom: 14,
                    borderRadius: 'var(--hj-r-sm)',
                    border: '1px solid var(--hj-yellow)',
                    background: 'var(--hj-yellow-tint)',
                    fontSize: 13,
                    color: 'var(--hj-ink)',
                  }}
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--hj-yellow-ink)' }} />
                  You started setup but Stripe still needs a few details before you can be paid.
                </div>
              )}
              <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.7 }}>
                <li>Takes about two minutes</li>
                <li>You keep 90% of every booking</li>
                <li>Held payouts release the moment you finish</li>
              </ul>
              <div style={{ marginBottom: 12 }}>
                <PayoutCountrySelect value={country || defaultPayoutCountry(status?.country)} onChange={setCountry} disabled={busy} />
              </div>
              <button
                onClick={handleOnboard}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 font-semibold"
                style={{
                  padding: '13px 20px',
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-yellow)',
                  border: 'none',
                  color: '#111',
                  fontSize: 15,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isPartial ? 'Finish payout setup' : 'Set up my payouts'}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </button>
            </>
          )}
        </div>

        {payout?.isReader && (payout.payoutMethod === 'credit' || !isReady) && (
          <div
            style={{
              marginTop: 14,
              padding: 18,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
              fontSize: 13.5,
              lineHeight: 1.5,
              color: 'var(--hj-ink)',
            }}
          >
            {payout.payoutMethod === 'credit' ? (
              <>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>You're taking your earnings as Hey Joe credit</div>
                <p style={{ margin: '0 0 12px' }}>
                  Your credit: <strong>{money(payout.balanceCents)}</strong>
                  {payout.earnedCents > 0 && payout.earnedCents !== payout.balanceCents ? ` (${money(payout.earnedCents)} earned from sessions)` : ''}.
                  {' '}Use it on classes, events and store items, or toward your Hey Joe Plus membership.
                </p>
                {payout.earnedCents > 0 && (
                  <button
                    onClick={applyToPlus}
                    disabled={creditBusy}
                    className="w-full flex items-center justify-center gap-2 font-semibold"
                    style={{ padding: '11px 18px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow)', border: 'none', color: '#111', fontSize: 14, cursor: creditBusy ? 'default' : 'pointer', marginBottom: 10 }}
                  >
                    {creditBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Put {money(payout.earnedCents)} toward my Plus membership
                  </button>
                )}
                <button
                  onClick={() => choosePayout('stripe')}
                  disabled={creditBusy}
                  style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Pay my future sessions out through Stripe instead
                </button>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Rather not connect Stripe?</div>
                <p style={{ margin: '0 0 12px' }}>
                  Take your earnings as Hey Joe credit instead. Use it on classes, events and store items, or toward your Hey Joe Plus membership.
                </p>
                <button
                  onClick={() => choosePayout('credit')}
                  disabled={creditBusy}
                  className="w-full flex items-center justify-center gap-2 font-semibold"
                  style={{ padding: '11px 18px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2, var(--hj-bg))', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14, cursor: creditBusy ? 'default' : 'pointer' }}
                >
                  {creditBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Take Hey Joe credit instead
                </button>
              </>
            )}
          </div>
        )}

        <p style={{ marginTop: 14, textAlign: 'center', fontSize: 12, color: 'var(--hj-ink-2)' }}>
          Payments are processed securely by Stripe. Hey Joe never sees your bank details.
        </p>
      </div>
    </main>
  );
};

export default ReaderPayoutsPage;
